import React, { useEffect, useMemo, useState } from 'react';
import BsModal from '../Common/BsModal';
import Notification from '../Common/Notification';
import ajaxVoucher from '../../util/remote/ajaxVoucher';

const INITIAL_FORM = { vouchersText: '', note: '' };

const RevokeVouchersModal = ({ show, onClose, onSuccess, codes = [], title = 'Revoke Vouchers' }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [notify, setNotify] = useState(null);

  // Right panel state
  const [pending, setPending] = useState([]); // [{id, voucher_code, reason, revoked_by, created_at}]
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingError, setPendingError] = useState('');
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  // Convert incoming codes to text
  const prefillText = useMemo(() => {
    if (Array.isArray(codes)) return codes.join(', ');
    if (typeof codes === 'string') return codes;
    return '';
  }, [codes]);

  // Parse input (commas/newlines), trim, dedupe
  const parsedCodes = useMemo(() => {
    const raw = form.vouchersText || '';
    const list = raw.split(/[,|\n]+/g).map(s => s.trim()).filter(Boolean);
    const unique = [];
    const seen = new Set();
    for (const c of list) {
      if (!seen.has(c)) {
        seen.add(c);
        unique.push(c.toUpperCase()); // normalize to uppercase early
      }
    }
    return unique;
  }, [form.vouchersText]);

  const invalidCodes = useMemo(() => {
    if (parsedCodes.length === 0) return [];
    const pattern = /^[A-Za-z0-9]{2,8}-[A-Za-z0-9]{4,12}$/; // adjust if needed
    return parsedCodes.filter(code => !pattern.test(code));
  }, [parsedCodes]);

  // -------- Pending list fetchers --------
  const fetchPending = async (silent = false) => {
    if (!silent) setPendingLoading(true);
    setPendingError('');
    const server_response = await ajaxVoucher.fetchRevocationQueue();
    setPendingLoading(false);
    if (server_response?.status === 'OK') {
    setPending(server_response.details);
    setLastRefreshedAt(new Date());
    } else {
    setPending([]);
    setPendingError(server_response?.message || 'Failed to load pending vouchers.');
    }
   
  };

  const refreshPending = () => fetchPending(false);

  useEffect(() => {
    if (show) {
      setForm(f => ({ ...INITIAL_FORM, vouchersText: prefillText }));
      setNotify(null);
      fetchPending(false);
    } else {
      setForm(INITIAL_FORM);
      setNotify(null);
      setPending([]);
      setPendingLoading(false);
      setPendingError('');
      setLastRefreshedAt(null);
    }
  }, [show, prefillText]);

  // -------- Submit (revoke) --------
  const handleSubmit = async () => {
    if (loading) return;
    if (parsedCodes.length === 0) {
      setNotify(<Notification type="danger" message="Please enter at least one voucher code." />);
      return;
    }

    if(form.note.length === 0) {
      setNotify(<Notification type="danger" message="Please enter a reason for revocation." />);
      return;
    }
    
    setLoading(true);
      const server_response = await ajaxVoucher.revokeVoucher({
        codes: parsedCodes,
        reason: form.note || ''
      });

      setLoading(false);
      if (server_response?.status === 'OK') {
        setNotify(<Notification type="success" message={server_response.message || 'Vouchers queued for revocation.'} />);
        setForm(f => ({ ...f, vouchersText: '' }));
        await fetchPending(true);
        onSuccess?.();
      } else {
        setNotify(<Notification type="danger" message={server_response?.message || 'Failed to queue vouchers.'} />);
      }
   
  };

  // -------- Render helpers (strictly using provided structure) --------
  const renderPendingItem = (item) => {
    const code = item.voucher_code;
    const note = item.reason;
    const by = item.revoked_by;
    const when = item.created_at; // string like "2025-08-27 13:38:22"

    // Safer date rendering if string has space
    let whenText = when;
    try {
      const d = new Date(when.replace(' ', 'T')); // "YYYY-MM-DDTHH:mm:ss"
      if (!isNaN(d.getTime())) whenText = d.toLocaleString();
    } catch (_) {}

    return (
      <li key={item.id} className="list-group-item d-flex flex-column py-2">
        <div className="d-flex align-items-center">
          <i className="fa fa-ticket mr-2" aria-hidden="true" />
          <strong className="mr-2">{code}</strong>
          <small className="text-muted ml-auto">{whenText}</small>
        </div>
        <div className="d-flex align-items-center mt-1">
          {!!note && <small className="text-muted">Reason: {note}</small>}
          {!!by && <small className="text-muted ml-auto">by #{by}</small>}
        </div>
      </li>
    );
  };

  return (
    <BsModal
      show={show}
      onClose={onClose}
      title={title}
      size="modal-lg"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleSubmit}
            disabled={loading || parsedCodes.length === 0}
          >
            <i className="fa fa-ban mr-1" />
            {loading ? ' Revoking…' : ` Revoke (${parsedCodes.length})`}
          </button>
        </>
      }
    >
      {notify}

      <div className="row">
        {/* LEFT: input form */}
        <div className="col-12 col-md-7">
          <div className="form-group">
            <label>
              Voucher Codes <small className="text-muted">(comma or newline separated)</small>
            </label>
            <textarea
              className={`form-control ${invalidCodes.length > 0 ? 'is-invalid' : ''}`}
              rows={6}
              placeholder="e.g. 25231-V6K6HY, 25231-A2VTHA"
              value={form.vouchersText}
              onChange={(e) => setForm(f => ({ ...f, vouchersText: e.target.value }))}
            />
            <small className="text-muted d-block mt-1">
              Paste or type multiple vouchers separated by commas or new lines.
            </small>
            <div className="d-flex justify-content-between mt-2">
              <small className="text-muted">
                Parsed: <strong>{parsedCodes.length}</strong>{' '}
                {invalidCodes.length > 0 && (
                  <span className="text-danger">
                    · Invalid: <strong>{invalidCodes.length}</strong>
                  </span>
                )}
              </small>
              {parsedCodes.length > 0 && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setForm(f => ({ ...f, vouchersText: parsedCodes.join(', ') }))}
                  title="Normalize separators"
                >
                  Normalize
                </button>
              )}
            </div>
            {invalidCodes.length > 0 && (
              <div className="invalid-feedback d-block">
                Some codes don’t match the expected pattern and may fail: {invalidCodes.slice(0, 5).join(', ')}
                {invalidCodes.length > 5 ? '…' : ''}
              </div>
            )}
          </div>

          <div className="form-group mb-3">
            <label>Reason / Note (optional)</label>
            <input
              type="text"
              className="form-control"
              placeholder="Short note for audit trail (optional)"
              value={form.note}
              onChange={(e) => setForm(f => ({ ...f, note: e.target.value }))}
            />
          </div>

          <div className="alert alert-info mb-0">
            Once submitted, vouchers are queued pending revocation. The voucher status will change after the revocation process is complete.
          </div>
        </div>

        {/* RIGHT: pending list */}
        <div className="col-12 col-md-5 mt-4 mt-md-0">
          <div className="d-flex align-items-center mb-2">
            <h6 className="mb-0">Pending Revocations</h6>
            <span className="badge badge-light ml-2">{pending.length}</span>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary ml-auto"
              onClick={refreshPending}
              disabled={pendingLoading}
              title="Refresh list"
            >
              <i className={`fa ${pendingLoading ? 'fa-spinner fa-spin' : 'fa-sync'}`} /> Refresh
            </button>
          </div>

          <div className="small text-muted mb-2">
            {lastRefreshedAt ? (
              <>Last refreshed: {lastRefreshedAt.toLocaleString()}</>
            ) : (
              <>Not refreshed yet</>
            )}
          </div>

          <div className="border rounded overflow-auto" style={{ maxHeight: 345}}>
            {pendingLoading && (
              <div className="p-3 text-center">
                <i className="fa fa-spinner fa-spin" aria-hidden="true" /> Loading pending vouchers…
              </div>
            )}

            {!pendingLoading && pendingError && (
              <div className="p-3 text-danger">
                <i className="fa fa-exclamation-circle mr-1" /> {pendingError}
              </div>
            )}

            {!pendingLoading && !pendingError && pending.length === 0 && (
              <div className="p-3 text-muted">No vouchers are currently pending revocation.</div>
            )}

            {!pendingLoading && !pendingError && pending.length > 0 && (
              <ul className="list-group list-group-flush">
                {pending.map((item) => renderPendingItem(item))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </BsModal>
  );
};

export default RevokeVouchersModal;
